import { IconBuilding, IconPlaneDeparture, IconPoint, IconWorld } from "@tabler/icons-react";

export const addComma = (num) => {
  return parseInt(num?.replace(/,/g, "")).toLocaleString();
};

export const extractNumber = (n) => {
  return n.replace(/[^0-9]/g, "");
};

export const checkMail = (mail) => {
  return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    mail
  );
};

// Plus-addressing and other unusual local-part symbols are not accepted for
// talent emails in production. Keep this stricter than checkMail.
export const TALENT_EMAIL_FORMAT_ERROR =
  "This talent’s email address isn’t in a valid format.";

export const isAllowedMessageRecipientEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  const trimmed = email.trim();
  if (/\s/.test(trimmed) || trimmed.includes("+")) return false;
  if (!checkMail(trimmed)) return false;
  return /^[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?\.[A-Za-z]{2,}$/.test(
    trimmed
  );
};

export const clientMessagesComposeHref = (email) => {
  if (!email) return "/client-messages";
  const params = new URLSearchParams({ email });
  return `/client-messages?${params.toString()}`;
};

// Query parsers treat unescaped "+" as a space. Restore "+" in the local-part
// so compose still works if a link was built without encoding.
export const emailFromQueryParam = (value) => {
  if (!value) return "";
  const at = value.lastIndexOf("@");
  if (at <= 0) return value;
  return `${value.slice(0, at).replace(/ /g, "+")}${value.slice(at)}`;
};

export const checkUrl = (link) => {
  return /[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/.test(
    link
  );
};
export const checkLinkedInUrl = (link) => {
  return /^https?:\/\/(www\.)?linkedin\.com\/(in|pub|company|school)\/[a-zA-Z0-9-]+\/?$/.test(
    link
  );
};
export const checkLinkedInUrlOptional = (link) => {
  if (!link) return true; // allow empty input
  return /^https?:\/\/(www\.)?linkedin\.com\/(in|pub|company|school)\/[a-zA-Z0-9_-]+\/?(?:\?.*)?$/.test(
    link
  );
};

export const isValidURL = (value) => {
  if (!value || typeof value !== "string") return false;

  const trimmed = value.trim();

  // Reject spaces anywhere in the URL
  if (/\s/.test(trimmed)) return false;

  let url = trimmed;

  // Support example.com or www.example.com
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  try {
    const parsed = new URL(url);

    return (
      ["http:", "https:"].includes(parsed.protocol) &&
      parsed.hostname.includes(".")
    );
  } catch {
    return false;
  }
};

export function toTitleCase(str) {
  return str
    .trim()
    .replace(
      /\w\S*/g,
      (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    );
}

export const numericOnly = (e) => {
  const cleaned = e.target.value.replace(/[^0-9]/g, '');
  if (e.target.value !== cleaned) e.target.value = cleaned;
};

export const validateAndCleanLinkedInUrl = (input) => {
  if (!input || typeof input !== "string") return null;

  try {
    const trimmedInput = input.trim();
    const normalizedInput = trimmedInput.startsWith("http")
      ? trimmedInput
      : `https://${trimmedInput}`;

    const url = new URL(normalizedInput);
    const hostname = url.hostname.toLowerCase();

    if (
      hostname !== "linkedin.com" &&
      hostname !== "www.linkedin.com"
    ) {
      return null;
    }

    if (!/^\/(in|pub|company|school)\/[a-zA-Z0-9_-]+\/?$/.test(url.pathname)) {
      return null;
    }

    url.search = "";
    url.hash = "";

    return url.toString().replace(/\/$/, "");
  } catch {
    return null;
  }
};






export const checkPassword = (link) => {
  // eslint-disable-next-line
  return /^(?=.*[a-z])(?=.*[0-9])((?=.*[A-Z])|(?=.*[-._!"`'#%&,:;<>=@{}~\$\(\)\*\+\/\\\?\[\]\^\|]))(?=.{10,})/.test(
    link
  );
};

export const checkPasswordNum = (num) => {
  return /\d/.test(num);
};

export const checkUpperCase = (value) => {
  return /^(?=.*[A-Z])/.test(value);
};

export const checkLowerCase = (value) => {
  return /^(?=.*[a-z])/.test(value);
};
export const checkName = (value) => {
  return /^[A-Za-z]{3,}$/.test(value);
};

export const checkSpecialChar = (char) => {
  // eslint-disable-next-line
  return /^(?=.*[-._!"`'#%&,:;<>=@{}~\$\(\)\*\+\/\\\?\[\]\^\|])/.test(char);
};

export const cpass = (pass, cpass) => {
  return pass === cpass;
};

export function convertToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const selectStyle = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    minHeight: 44,
    height: 44,
    borderRadius: 10,
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.15)",
    },
    "&:focus": {
      borderColor: "rgba(0, 0, 0, 0.15)",
      outline: "1px solid #030659 !important",
    },
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    height: 44,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 12,
    paddingRight: 12,
    display: "flex",
    alignItems: "center",
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    padding: 0,
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    fontSize: 14,
    position: "absolute",
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    height: 44,
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    lineHeight: "normal",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: 14,
    cursor: "pointer",
    padding: "8px 15px",
    fontWeight: "300",
    backgroundColor: state.isSelected ? "#ECEDFB" : "#fff",
    color: "#000000",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};
export const locationSelectStyle = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    minHeight: 44,
    height: 44,
    borderRadius: 10,
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.15)",
    },
    "&:focus": {
      borderColor: "rgba(0, 0, 0, 0.15)",
      outline: "1px solid #030659 !important",
    },
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    height: 44,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 12,
    paddingRight: 12,
    display: "flex",
    alignItems: "center",
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    paddingBottom: 0,
    position: "absolute",
    top: "5px",
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    fontSize: 14,
    position: "absolute",
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    height: 44,
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    lineHeight: "normal",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: 14,
    cursor: "pointer",
    padding: "8px 15px",
    fontWeight: "300",
    backgroundColor: state.isSelected ? "#ECEDFB" : "#fff",
    color: "#000000",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};


export function decodeHTMLEntities(text) {
  const div = document.createElement("div");
  div.innerHTML = text;
  return div.textContent;
}


export const getSelectStyle = (controlHeight = 44) => ({
  control: (baseStyles, state) => ({
    ...baseStyles,
    minHeight: controlHeight,
    height: controlHeight,
    borderRadius: 10,
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.15)",
    },
    "&:focus": {
      borderColor: "rgba(0, 0, 0, 0.15)",
      outline: "1px solid #030659 !important",
    },
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    height: controlHeight,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 12,
    paddingRight: 12,
    display: "flex",
    alignItems: "center",
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    padding: 0,
    height: controlHeight || 40,
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    height: controlHeight,
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    lineHeight: "normal",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: 14,
    cursor: "pointer",
    borderBottom: "0px",
    padding: "8px 15px",
    fontWeight: "300",
    backgroundColor: state.isSelected ? "#ECEDFB" : "#fff",
    color: "#000000",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
});


export const tagsSelectStyle = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderRadius: 8,
    boxShadow: "none",
    borderColor: "rgba(0, 0, 0, 0.15)",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.15)",
    },
    "&:focus": {
      borderColor: "rgba(0, 0, 0, 0.15)",
      outline: "1px solid #030659 !important",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#030659",
    borderRadius: "4px",
    padding: "2px 6px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#fff",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#fff",
  }),
  indicatorSeparator: () => ({ display: "none" }),
}

export function currencyFormat(num) {
  // Convert to number if it's a valid numeric string
  const value = Number(num);

  // If the value is not a valid number, return the original input
  if (isNaN(value)) return num;

  return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const determineFileType = (file) => {
  const mimeType = file.type;
  if (mimeType.startsWith("image/")) {
    return "image";
  } else if (mimeType === "application/pdf") {
    return "resume";
  } else if (mimeType.startsWith("text/")) {
    return "address";
  } else if (mimeType.startsWith("application/")) {
    return "identity";
  } else {
    return "other";
  }
};

export const validateSocialField = (name, value) => {
  let errorMsg = "";

  switch (name) {
    case "facebook":
      if (
        !/^(https?:\/\/)?(www\.)?facebook\.com\/[a-zA-Z0-9(.?)?]/.test(value)
      ) {
        errorMsg =
          "Invalid Facebook URL. Ensure it starts with https://www.facebook.com/username";
      }
      break;

    case "instagram":
      if (
        !/^(https?:\/\/)?(www\.)?instagram\.com\/[a-zA-Z0-9_]+\/?$/.test(
          value
        )
      ) {
        errorMsg =
          "Invalid Instagram URL. Ensure it starts with https://www.instagram.com/username";
      }
      break;

    case "linkedIn":
      if (
        !/^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-_.]+\/?$/.test(
          value
        )
      ) {
        errorMsg =
          "Invalid LinkedIn URL. Ensure it starts with https://www.linkedin.com/in/username";
      }
      break;

    case "twitter": // Supporting X (Twitter)
      if (
        !/^(https?:\/\/)?(www\.)?(twitter|x)\.com\/[a-zA-Z0-9_]+\/?$/.test(
          value
        )
      ) {
        errorMsg =
          "Invalid X (Twitter) URL. Ensure it starts with https://www.x.com/username or https://www.twitter.com/username";
      }
      break;

    default:
      break;
  }

  return errorMsg;
};


export const getLinkedInName = (url) => {
  try {
    const parsedUrl = new URL(url);
    const parts = parsedUrl.pathname.split("/").filter(Boolean);

    if (parts[0] === "in") {
      // Personal profile: linkedin.com/in/username
      return parts[1] || "LinkedIn Profile";
    } else if (parts[0] === "company") {
      // Company page: linkedin.com/company/company-name
      return parts[1] || "LinkedIn Company";
    }

    return "LinkedIn";
  } catch {
    return "LinkedIn";
  }
};


export const getProgressPercent = (startDateStr, endDateStr, durationStr) => {
  const now = new Date();
  const startDate = new Date(startDateStr);
  let endDate;

  if (endDateStr) {
    endDate = new Date(endDateStr);
  } else if (durationStr) {
    const [amountStr, unit] = durationStr.toLowerCase().split(" ");
    const amount = parseInt(amountStr);

    endDate = new Date(startDate); // clone start date

    if (unit.includes("month")) {
      endDate.setMonth(endDate.getMonth() + amount);
    } else if (unit.includes("year")) {
      endDate.setFullYear(endDate.getFullYear() + amount);
    } else if (unit.includes("week")) {
      endDate.setDate(endDate.getDate() + amount * 7);
    } else if (unit.includes("day")) {
      endDate.setDate(endDate.getDate() + amount);
    } else {
      return 0;
    }
  } else {
    return 0;
  }

  // Progress calculation
  if (now < startDate) return 0;
  if (now > endDate) return 100;

  const totalTime = endDate.getTime() - startDate.getTime();
  const elapsedTime = now.getTime() - startDate.getTime();

  const percent = (elapsedTime / totalTime) * 100;
  return Math.round(percent);
};


// Helper function to count words in description
export const countWords = (text) => {
  return text?.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Get percentage value
 * @param {number} value 
 * @param {number} max 
 * @returns 
 */
export const getPercent = (value = 0, max = 100) => {
  if (max === 0) return 0;
  return Math.round(((typeof value === "boolean" ? (value ? max : 0) : value) / max) * 100);
};


export function shareProfile(platform, msg) {
  const url = `${window.location.origin}/profile/` ;
  const profileURL = encodeURIComponent(url);
  const text = encodeURIComponent( msg || "Check out my Profile!");

  let shareUrl = "";

  switch (platform) {
    case "whatsapp":
      shareUrl = `https://wa.me/?text=${text}%20${profileURL}`;
      break;

    case "linkedin":
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${profileURL}`;
      break;

    case "x":
      shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${profileURL}`;
      break;

    case "facebook":
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${profileURL}`;
      break;

    default:
    break;
  }

  window.open(shareUrl, "_blank");
}

export function parseYouTube(url) {
  if (!url) {
    return { videoId: null, playlistId: null }
  }

  let u

  try {
    u = new URL(url)
  } catch {
    return { videoId: null, playlistId: null }
  }

  const videoId =
    u.searchParams.get('v') ||
    u.pathname.split('/').pop()

  const playlistId = u.searchParams.get('list')

  return { videoId, playlistId }
}

export const parseYouTubeURL = (url) => {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    
    // Check if it's a YouTube URL
    const isYoutube = 
      urlObj.hostname === 'www.youtube.com' || 
      urlObj.hostname === 'youtube.com' || 
      urlObj.hostname === 'm.youtube.com' ||
      urlObj.hostname === 'youtu.be';
    
    if (!isYoutube) {
      return url; // Not a YouTube URL, return as is
    }
    
    // Get video ID
    let videoId = urlObj.searchParams.get('v');
    
    // Handle youtu.be short links
    if (urlObj.hostname === 'youtu.be') {
      videoId = urlObj.pathname.slice(1).split('?')[0];
    }
    
    if (videoId) {
      // Keep only the 'v' parameter, remove list, index, etc.
      const cleanUrl = new URL(url);
      const newParams = new URLSearchParams();
      newParams.set('v', videoId);
      cleanUrl.search = newParams.toString();
      
      return cleanUrl.toString();
    }
    
    return url;
  } catch (error) {
    return url;
  }
};


const loadImageWithCORS = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => resolve(img);
    img.onerror = () => {
      resolve(null);
    };
    
    img.src = src;
  });
};


export const preloadImagesWithCORS = async (element) => {
  const images = Array.from(element.querySelectorAll('img'));
  
  await Promise.all(
    images.map(async (img) => {
      const src = img.src;
      
      try {
        const loadedImg = await loadImageWithCORS(src);
        if (loadedImg) {
          img.crossOrigin = 'anonymous';
        }
      } catch (error) {

      }
    })
  );
};

export const formatAnswers = (test, answers) => {
  try {
    if (
      !test ||
      !answers ||
      typeof test !== "object" ||
      typeof answers !== "object" ||
      !test.questions ||
      !Array.isArray(test.questions)
    ) {
      return "";
    }

    const grouped = {};

    // Group questions by skill
    for (const q of test.questions) {
      if (!grouped[q.skill]) {
        grouped[q.skill] = [];
      }
      grouped[q.skill].push(q);
    }

    let result = "";

    // Format each skill group
    for (const [skill, questions] of Object.entries(grouped)) {
      result += `${skill}\n`;
      for (const q of questions) {
        if (q && typeof q === "object" && q.id && q.text) {
          const answer = answers[q.id] || "No answer provided";
          result += `Q. ${q.text}\nA. ${answer}\n\n`;
        }
      }
    }

    return result.trim();
  } catch (e) {
    console.error("An error occurred formatting answers");
    return "";
  }
};


export const formatPsychometricAnswers = (test, answers) => {
  try {
    if (
      !test ||
      !answers ||
      typeof test !== 'object' ||
      typeof answers !== 'object' ||
      !test.questions ||
      !Array.isArray(test.questions)
    ) {
      return "";
    }

    const grouped = {};

    // Group questions by type
    for (const q of test.questions) {
      if (!grouped[q.type]) {
        grouped[q.type] = [];
      }
      grouped[q.type].push(q);
    }

    let result = "";
    
    // Format each type group
    for (const [type, questions] of Object.entries(grouped)) {
      result += `${type}\n`;
      for (const q of questions) {
        if (
          q &&
          typeof q === 'object' &&
          q.id &&
          q.text
        ) {
          const answer = answers[q.id] || "No answer provided";
          result += `Q. ${q.text}\nA. ${answer}\n\n`;
        }
      }
    }

    return result.trim();
  } catch (e) {
    console.error("An error occurred formatting psychometric answers");
    return "";
  }
};


export const getCurrencySymbol = (currency) => {
  if (currency === "USD") return "$";
  if (currency === "NGN") return "₦";
  return "";
};

export const fmtCurrency = (amount, currency) =>
  currency === 'NGN'
    ? `₦${Number(amount).toLocaleString()}`
    : `$${Number(amount).toLocaleString()}`;

export const AVATAR_THEMES = [
  { bg: '#eef2ff', fg: '#1d4ed8' },
  { bg: '#ecfdf5', fg: '#059669' },
  { bg: '#fef3c7', fg: '#b45309' },
  { bg: '#fce7f3', fg: '#be185d' },
  { bg: '#f3e8ff', fg: '#7c3aed' },
  { bg: '#e0f2fe', fg: '#0369a1' },
  { bg: '#fef2f2', fg: '#dc2626' },
  { bg: '#f0fdfa', fg: '#0d9488' },
];
 
export function themeFor(name) {
  let s = 0;
  for (let i = 0; i < name?.length; i++) s += name?.charCodeAt(i);
  return AVATAR_THEMES[s % AVATAR_THEMES?.length];
}
 
export function initialsFor(role) {
  const w = String(role || "").trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "";
  return (w[0]?.charAt(0) + (w[1] ? w[1]?.charAt(0) : "")).toUpperCase();
}

// Maps a work-mode string -> the @tabler/icons-react component shown in its chip
export const MODE_ICON = {
  Onsite: IconBuilding,
  'Work Remotely': IconWorld,
  'Open to relocate': IconPlaneDeparture,
};
export function modeIconFor(mode) {
  return MODE_ICON[mode] || IconPoint;
}

export const PRIORITY_ICON_KEY = {
  'Work Life Balance': 'scale',
  'Career Growth': 'trendingUp',
  'Work Benefits': 'gift',
  'Remote Flexibility': 'home',
  'Learning & Development': 'book',
  'Leadership Opportunities': 'crown',
  'Competitive Salary': 'coins',
  'Innovation & Creativity': 'bulb',
  'Team Culture': 'users',
};
 
export const SCORE_TRAITS = ['Stress Tolerance', 'Collaboration', 'Judgement', 'Teamwork', 'Responsibility', 'Adaptability', 'Clarity', 'Creativity', 'Communication'];
 
/** initials('Jane', 'Doest') -> 'JD' */
export function getNameInitials(firstName, lastName) {
  const first = String(firstName ?? "").charAt(0).toUpperCase();
  const last = String(lastName ?? "").charAt(0).toUpperCase();

  return `${first}${last}`;
}

/** Password strength scorer */
export function scorePasswordStrength(value) {
  let score = 0;
  if (value.length >= 8) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[0-9]/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;
  return score;
}

export function diffFields(initial, current) {
	const changed = {};
	for (const key of Object.keys(current)) {
		if (current[key] !== initial[key]) {
			changed[key] = current[key];
		}
	}
	return changed;
}
 

// set
export const setCookie = (name, value, days) => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

// get
export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};
